import { CloseFunction, DismissFunction } from '@app/shared/dialog/dialog';
import { NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { DialogComponent } from './dialog-component.directive';

export class DialogRef<T extends DialogComponent, Input, Output> {
  public readonly componentInstance: T;

  private onCloseCallback: CloseFunction<Output>;
  private onDismissCallback: DismissFunction;

  private dismissSubscription?: Subscription;

  constructor(private modalRef: NgbModalRef) {
    this.componentInstance = modalRef.componentInstance;

    this.dismissSubscription = this.modalRef.dismissed?.subscribe((reason?: any) => {
      this.componentInstance.onDismissed(reason);
    });

    this.componentInstance.setCallbacks(
      (result) => {
        if (this.onCloseCallback) {
          this.onCloseCallback(result);
        }
      },
      (reason) => {
        if (this.onDismissCallback) {
          this.onDismissCallback(reason);
        }
      }
    );
  }

  /**
   * Closes the modal with an optional result value.
   */
  close(result?: Output): void {
    this.componentInstance.close(result);
  }

  /**
   * Dismisses the modal with an optional reason value.
   * @param reason
   */
  dismiss(reason?: any): void {
    this.dismissSubscription.unsubscribe();

    this.componentInstance.dismiss(reason);
  }

  /**
   * Register a callback function that is called when the modal is closed successfully.
   * @param callback Function that is called with an optional result.
   */
  onClose(callback: (result?: Output) => void): this {
    this.onCloseCallback = callback;

    return this;
  }

  /**
   * Register a callback function that is called when the modal is dismissed.
   * @param callback Function that is called with an optional reason.
   */
  onDismiss(callback: (reason?: any) => void): this {
    this.onDismissCallback = callback;

    return this;
  }

  /**
   * Create a promise that is resolved when the dialog is closed or dismissed.
   * The promise will contain a string indicating whether it was closed or dismissed.
   */
  toPromise(): Promise<'closed' | 'dismissed'> {
    return new Promise((resolve) => {
      this.onClose(() => resolve('closed'));
      this.onDismiss(() => resolve('dismissed'));
    });
  }

  /**
   * Pass the given object as input to the created modalRef.
   * @param inputs Object whose properties are passed to the modal component.
   */
  withInput(inputs: Input): this {
    for (const key in inputs) {
      if (inputs.hasOwnProperty(key)) {
        this.modalRef.componentInstance[key] = inputs[key];
      }
    }

    return this;
  }
}
